import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Search, Loader2, TrendingUp, Sparkles, Music, Users, Zap, ArrowRight, Play, Eye, Heart, MessageCircle, Share2, X, Calendar, Download, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';

// Video Grid Component with auto-pause functionality
const VideoGrid = ({ videos }) => {
  const videoRefs = useRef([]);

  useEffect(() => {
    // Set up event listeners for all videos
    const videoElements = videoRefs.current.filter(Boolean);
    
    const handlePlay = (playingVideo) => {
      videoElements.forEach((video) => {
        if (video !== playingVideo) {
          video.pause();
          video.classList.remove('active');
        } else {
          video.classList.add('active');
        }
      });
    };

    // Store handlers for cleanup
    const handlers = videoElements.map((video) => {
      const handler = () => handlePlay(video);
      video.addEventListener('play', handler);
      return { video, handler };
    });

    // Cleanup
    return () => {
      handlers.forEach(({ video, handler }) => {
        video.removeEventListener('play', handler);
      });
    };
  }, [videos]);

  return (
    <div className="flex flex-row gap-4 justify-start pb-4 overflow-x-auto -mx-6 px-6">
      {videos.map((video, index) => (
        <div
          key={video.id}
          className="flex-shrink-0 w-64 border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow bg-white"
        >
          <div className="flex flex-col">
            {/* Video Player */}
            <div className="relative w-full bg-gray-100 rounded-t-lg overflow-hidden" style={{ aspectRatio: '9/16' }}>
              {video.videoUrl ? (
                <video
                  ref={(el) => {
                    videoRefs.current[index] = el;
                  }}
                  src={video.videoUrl}
                  className="smart-video w-full h-full object-cover"
                  controls
                  playsInline
                  preload="metadata"
                  onError={(e) => {
                    // Hide video and show placeholder on error
                    e.target.style.display = 'none';
                    const placeholder = e.target.nextElementSibling;
                    if (placeholder) placeholder.style.display = 'flex';
                  }}
                >
                  Your browser does not support the video tag.
                </video>
              ) : video.coverUrl ? (
                <>
                  <img
                    src={video.coverUrl}
                    alt={video.caption || 'Video thumbnail'}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const placeholder = e.target.nextElementSibling;
                      if (placeholder) placeholder.style.display = 'flex';
                    }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 pointer-events-none">
                    <Play className="w-12 h-12 text-white" />
                  </div>
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-200">
                  <div className="text-center">
                    <Play className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-xs text-gray-500">No preview</p>
                  </div>
                </div>
              )}
              {/* Fallback placeholder (hidden by default) */}
              <div className="hidden absolute inset-0 bg-gray-200 flex items-center justify-center">
                <div className="text-center">
                  <Play className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-xs text-gray-500">Video unavailable</p>
                </div>
              </div>
            </div>
            
            {/* Video Info */}
            <div className="flex-1 p-4">
              <div className="mb-2">
                <p className="text-sm font-medium text-gray-900 mb-1">
                  @{video.author}
                </p>
                {video.postDate && (
                  <div className="flex items-center text-xs text-gray-500 mb-2">
                    <Calendar className="w-3 h-3 mr-1" />
                    <span>
                      {new Date(video.postDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                )}
                {video.caption && (
                  <p className="text-sm text-gray-700 line-clamp-2">
                    {video.caption}
                  </p>
                )}
              </div>

              {/* Engagement Metrics */}
              <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-gray-200">
                <div className="flex items-center text-xs text-gray-600">
                  <Eye className="w-3 h-3 mr-1" />
                  <span className="font-medium">{video.engagement.views.toLocaleString()}</span>
                </div>
                <div className="flex items-center text-xs text-gray-600">
                  <Heart className="w-3 h-3 mr-1" />
                  <span className="font-medium">{video.engagement.likes.toLocaleString()}</span>
                </div>
                <div className="flex items-center text-xs text-gray-600">
                  <MessageCircle className="w-3 h-3 mr-1" />
                  <span className="font-medium">{video.engagement.comments.toLocaleString()}</span>
                </div>
                <div className="flex items-center text-xs text-gray-600">
                  <Share2 className="w-3 h-3 mr-1" />
                  <span className="font-medium">{video.engagement.shares.toLocaleString()}</span>
                </div>
              </div>

              {/* Video Link */}
              {video.videoUrl && (
                <a
                  href={video.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center text-xs text-purple-600 hover:text-purple-700"
                >
                  Watch on TikTok
                  <ArrowRight className="w-3 h-3 ml-1" />
                </a>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const TrendDiscovery = () => {
  const { user } = useAuth();
  const [selectedKeyword, setSelectedKeyword] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [selectedPhrase, setSelectedPhrase] = useState(null);
  const [customPhrase, setCustomPhrase] = useState('');
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [videos, setVideos] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [phraseSearchProgress, setPhraseSearchProgress] = useState(0);
  const [videoDownloadProgress, setVideoDownloadProgress] = useState(0);

  // Define the keyword sections
  const sections = [
    {
      id: 'seed-words',
      name: 'Seed Words',
      description: 'Keywords that surface trending phrases and viral content',
      icon: TrendingUp,
      color: 'from-purple-500 to-pink-500',
      keywords: [
        // From Tier 1 — Universal Viral Triggers
        { word: 'why', reason: 'controversy, curiosity' },
        { word: 'what', reason: 'discovery' },
        { word: 'this', reason: 'reactions' },
        { word: 'everyone', reason: 'mass behavior' },
        { word: 'nobody', reason: 'meme setups' },
        { word: 'i just', reason: 'emotional' },
        { word: 'i was', reason: 'storytime' },
        { word: 'did', reason: 'events' },
        { word: 'how', reason: 'learning trends' },
        { word: 'watch', reason: 'anticipation' },
        // From Tier 2 — Format-Based Trend Engines
        { word: 'pov', reason: 'skits' },
        { word: 'when', reason: 'reaction memes' },
        { word: 'tell me', reason: 'confessionals' },
        { word: 'i tried', reason: 'experiments' },
        { word: 'this is', reason: 'reveals' },
        { word: 'i never', reason: 'shock' },
        { word: 'my', reason: 'routines' },
        { word: 'day in', reason: 'lifestyle' },
        { word: 'things', reason: 'list formats' },
        // From Tier 4 — Character-Driven Virality
        { word: 'the girl' },
        { word: 'the guy' },
        { word: 'he' },
        { word: 'she' },
        { word: 'my boyfriend' },
        { word: 'my girlfriend' },
        { word: 'my mom' },
        { word: 'my boss' }
      ]
    },
    {
      id: 'prefixes',
      name: 'Prefixes',
      description: 'Single character prefix search (a-z, 0-9)',
      icon: Search,
      color: 'from-blue-500 to-cyan-500',
      keywords: [
        // Letters a-z (26 characters)
        { word: 'a' },
        { word: 'b' },
        { word: 'c' },
        { word: 'd' },
        { word: 'e' },
        { word: 'f' },
        { word: 'g' },
        { word: 'h' },
        { word: 'i' },
        { word: 'j' },
        { word: 'k' },
        { word: 'l' },
        { word: 'm' },
        { word: 'n' },
        { word: 'o' },
        { word: 'p' },
        { word: 'q' },
        { word: 'r' },
        { word: 's' },
        { word: 't' },
        { word: 'u' },
        { word: 'v' },
        { word: 'w' },
        { word: 'x' },
        { word: 'y' },
        { word: 'z' },
        // Digits 0-9 (10 characters)
        { word: '0' },
        { word: '1' },
        { word: '2' },
        { word: '3' },
        { word: '4' },
        { word: '5' },
        { word: '6' },
        { word: '7' },
        { word: '8' },
        { word: '9' }
      ]
    }
  ];

  const handleKeywordClick = async (keyword) => {
    if (loading) return;
    
    setSelectedKeyword(keyword);
    setLoading(true);
    setResults(null);
    setVideos(null);
    setSelectedPhrase(null);
    setPhraseSearchProgress(0);

    // Start progress timer (10 seconds)
    let progressInterval = null;
    progressInterval = setInterval(() => {
      setPhraseSearchProgress(prev => {
        if (prev >= 100) {
          if (progressInterval) clearInterval(progressInterval);
          return 100;
        }
        return prev + 10; // Increment by 10% every second (10 seconds total
      });
    }, 1000);

    try {
      const response = await fetch('/api/trend-discovery/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          keyword: keyword,
          count: 20
        })
      });

      const data = await response.json();

      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
      setPhraseSearchProgress(100);

      if (data.success) {
        // Filter out trends with less than 3 words
        const filteredResults = {
          ...data,
          results: data.results.filter(item => {
            const wordCount = item.phrase.trim().split(/\s+/).length;
            return wordCount >= 3;
          })
        };
        filteredResults.count = filteredResults.results.length;
        setResults(filteredResults);
        toast.success(`Found ${filteredResults.count} trending phrases for "${keyword}"`);
      } else {
        toast.error(data.error || 'Failed to fetch trending phrases');
        setResults(null);
      }
    } catch (error) {
      console.error('Error fetching trending phrases:', error);
      toast.error('Failed to fetch trending phrases');
      setResults(null);
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
    } finally {
      setLoading(false);
      setTimeout(() => setPhraseSearchProgress(0), 1000); // Reset after a brief delay
    }
  };

  const handlePhraseClick = async (phrase) => {
    if (loadingVideos) return;
    
    setSelectedPhrase(phrase);
    setLoadingVideos(true);
    setVideos(null);
    setShowVideoModal(true);
    setVideoDownloadProgress(0);

    // Start progress timer (30 seconds)
    let progressInterval = null;
    progressInterval = setInterval(() => {
      setVideoDownloadProgress(prev => {
        if (prev >= 100) {
          if (progressInterval) clearInterval(progressInterval);
          return 100;
        }
        return prev + (100 / 30); // Increment by ~3.33% every second (30 seconds total)
      });
    }, 1000);

    try {
      const response = await fetch('/api/trend-discovery/videos', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          phrase: phrase,
          limit: 5
        })
      });

      const data = await response.json();

      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
      setVideoDownloadProgress(100);

      if (data.success) {
        // Debug: Log video URLs
        if (data.videos && data.videos.length > 0) {
          console.log('Video URLs received:', data.videos.map(v => ({ id: v.id, videoUrl: v.videoUrl, coverUrl: v.coverUrl })));
        }
        setVideos(data);
        if (data.count > 0) {
          toast.success(`Found ${data.count} videos for "${phrase}"`);
        } else {
          toast.info(`No videos found for "${phrase}"`);
        }
      } else {
        toast.error(data.error || 'Failed to fetch videos');
        setVideos(null);
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast.error('Failed to fetch videos');
      setVideos(null);
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
    } finally {
      setLoadingVideos(false);
      setTimeout(() => setVideoDownloadProgress(0), 1000); // Reset after a brief delay
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Trend Discovery</h1>
              <p className="mt-1 text-sm text-gray-500">
                Click on any keyword to discover trending phrases on TikTok
              </p>
            </div>
            {user && (
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600">{user.email}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Custom Search and Keyword Sections */}
          <div className="lg:col-span-2 space-y-6">
            {/* Custom Phrase Search Section */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Search className="w-5 h-5 mr-2 text-purple-500" />
                  Custom Trend Search
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Enter any phrase to discover trending variations
                </p>
              </div>
              <div className="px-6 py-4">
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={customPhrase}
                    onChange={(e) => setCustomPhrase(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && customPhrase.trim() && !loading) {
                        handleKeywordClick(customPhrase.trim());
                      }
                    }}
                    placeholder="Enter a phrase (e.g., 'summer fashion', 'cooking tips')"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    disabled={loading}
                  />
                  <button
                    onClick={() => {
                      if (customPhrase.trim() && !loading) {
                        handleKeywordClick(customPhrase.trim());
                      }
                    }}
                    disabled={loading || !customPhrase.trim()}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium flex items-center gap-2"
                  >
                    {loading && selectedKeyword === customPhrase.trim() ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4" />
                        Trend Search
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Keyword Sections */}
            {sections.map((section) => {
              const Icon = section.icon;
              const isExpanded = activeSection === section.id;
              
              return (
                <div
                  key={section.id}
                  className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200"
                >
                  {/* Section Header */}
                  <button
                    onClick={() => setActiveSection(isExpanded ? null : section.id)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`p-3 rounded-lg bg-gradient-to-br ${section.color} text-white`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="text-left">
                        <h2 className="text-lg font-semibold text-gray-900">{section.name}</h2>
                        <p className="text-sm text-gray-500">{section.description}</p>
                      </div>
                    </div>
                    <ArrowRight
                      className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                    />
                  </button>

                  {/* Keywords Grid */}
                  {isExpanded && (
                    <div className="px-6 pb-6 pt-2">
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {section.keywords.map((item, index) => {
                          const isSelected = selectedKeyword === item.word;
                          const isLoading = loading && isSelected;
                          
                          return (
                            <button
                              key={index}
                              onClick={() => handleKeywordClick(item.word)}
                              disabled={loading}
                              className={`
                                relative px-4 py-3 rounded-lg border-2 transition-all
                                ${isSelected
                                  ? 'border-purple-500 bg-purple-50 text-purple-700'
                                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                                }
                                ${loading && !isSelected ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                ${isLoading ? 'animate-pulse' : ''}
                              `}
                            >
                              <div className="font-semibold text-sm">{item.word}</div>
                              {item.reason && (
                                <div className="text-xs text-gray-500 mt-1">{item.reason}</div>
                              )}
                              {isLoading && (
                                <div className="absolute top-2 right-2">
                                  <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Right Column - Results */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md border border-gray-200 sticky top-8">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Search className="w-5 h-5 mr-2 text-purple-500" />
                  Trending Phrases
                </h3>
              </div>

              <div className="p-6">
                {!selectedKeyword && !loading && (
                  <div className="text-center py-12">
                    <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">
                      Click on any keyword to discover trending phrases
                    </p>
                  </div>
                )}

                {loading && (
                  <div className="text-center py-12">
                    <Loader2 className="w-12 h-12 text-purple-500 animate-spin mx-auto mb-4" />
                    <p className="text-gray-500 mb-2">
                      Searching for trends with "{selectedKeyword}"...
                    </p>
                    <div className="w-full max-w-xs mx-auto">
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div
                          className="bg-purple-500 h-2.5 rounded-full transition-all duration-300"
                          style={{ width: `${phraseSearchProgress}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        {Math.round(phraseSearchProgress)}% • {Math.max(0, 10 - Math.round(phraseSearchProgress / 10))}s remaining
                      </p>
                    </div>
                  </div>
                )}

                {results && results.results && (
                  <div>
                    <div className="mb-4 pb-4 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            Keyword: <span className="text-purple-600">"{results.keyword}"</span>
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Found {results.count} trending phrases
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 max-h-[600px] overflow-y-auto">
                      {results.results.map((item) => (
                        <div
                          key={item.id}
                          className="w-full p-3 rounded-lg bg-gray-50 border border-gray-200 hover:bg-gray-100 hover:border-purple-300 transition-colors"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">
                                {item.phrase}
                              </p>
                              {item.extraInfo?.recall_reason && (
                                <p className="text-xs text-gray-500 mt-1">
                                  {item.extraInfo.recall_reason.split('|').join(' • ')}
                                </p>
                              )}
                            </div>
                            <div className="ml-2 flex-shrink-0">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                #{item.id}
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-2 mt-2">
                            <button
                              onClick={() => handlePhraseClick(item.phrase)}
                              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-md transition-colors"
                            >
                              <Download className="w-3 h-3" />
                              Download
                            </button>
                            <button
                              onClick={() => {
                                const tiktokUrl = `https://www.tiktok.com/search?q=${encodeURIComponent(item.phrase)}`;
                                window.open(tiktokUrl, '_blank', 'noopener,noreferrer');
                              }}
                              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors"
                            >
                              <ExternalLink className="w-3 h-3" />
                              Go to TikTok
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {results.count > 0 && results.count < results.requestedCount && (
                      <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-xs text-yellow-800">
                          ⚠️ TikTok's preview endpoint returned {results.count} results (requested {results.requestedCount}).
                          The autocomplete API typically returns 12-13 results maximum.
                        </p>
                      </div>
                    )}
                    {results.count === 0 && (
                      <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-xs text-red-800">
                          ⚠️ No results returned. This might be due to:
                          <ul className="list-disc list-inside mt-2 space-y-1">
                            <li>Playwright browsers not installed (run: npx playwright install)</li>
                            <li>Network/authentication issues</li>
                            <li>Keyword too specific or no matches found</li>
                          </ul>
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {showVideoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Top Videos for "{selectedPhrase}"
                </h3>
                {videos && videos.count > 0 && (
                  <p className="text-sm text-gray-500 mt-1">
                    Found {videos.count} videos
                  </p>
                )}
              </div>
              <button
                onClick={() => {
                  setShowVideoModal(false);
                  setVideos(null);
                  setSelectedPhrase(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-x-auto">
              {loadingVideos && (
                <div className="text-center py-12">
                  <Loader2 className="w-12 h-12 text-purple-500 animate-spin mx-auto mb-4" />
                  <p className="text-gray-500 mb-2">
                    Searching and downloading videos...
                  </p>
                  <div className="w-full max-w-xs mx-auto">
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className="bg-purple-500 h-2.5 rounded-full transition-all duration-300"
                        style={{ width: `${videoDownloadProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {Math.round(videoDownloadProgress)}% • {Math.max(0, 30 - Math.round(videoDownloadProgress / (100/30)))}s remaining
                    </p>
                  </div>
                </div>
              )}

              {!loadingVideos && videos && videos.count === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-500">
                    No videos found for "{selectedPhrase}"
                  </p>
                </div>
              )}

              {!loadingVideos && videos && videos.videos && videos.videos.length > 0 && (
                <VideoGrid videos={videos.videos} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrendDiscovery;
